import { useEffect, useMemo } from 'react';
import { type ListBoxSectionItem } from '@direct-frontend/components';
import {
    type OpenApiSchemaMenuLinkType,
    type OpenApiSchemaMenuPossibleCategoryLink,
    type OpenApiSchemaMenuPossibleCollectionLink,
    type OpenApiSchemaMenuPossiblePageLink,
} from '#shared/api/schema';
import { mapMenuCategoriesToTreeForSelect } from '#shared/lib/menu';
import { trimAndLowerString } from '#shared/lib/string';
import { showError } from '#shared/lib/toast';
import { useGetMenuLinks } from '../api';
import { MenuItemLinkTypeIcon } from '../constants';
import { splitMenuCollectionsToSections } from '../lib';

type InternalLinkSectionKey = Exclude<OpenApiSchemaMenuLinkType, 'external'> | 'collection-auto';
type InternalLinkSection = ListBoxSectionItem<InternalLinkSectionKey, string>;

export const useLoadMenuInternalLinks = (
    searchString = ''
): {
    isLoading: boolean;
    items: InternalLinkSection[];
    categories: OpenApiSchemaMenuPossibleCategoryLink[];
    pages: OpenApiSchemaMenuPossiblePageLink[];
    collections: OpenApiSchemaMenuPossibleCollectionLink[];
} => {
    const {
        data: { page: pages = [], category: categories = [], collection: collections = [] } = {},
        isFetching: isFetchingCategories,
        isError: isErrorMenuLinks,
        error: errorMenuLinks,
    } = useGetMenuLinks();

    const trimmedSearchString = useMemo(() => trimAndLowerString(searchString), [searchString]);

    useEffect(() => {
        if (isErrorMenuLinks) {
            showError({ label: 'Ошибка загрузки списка ссылок' }, errorMenuLinks);
        }
    }, [errorMenuLinks, isErrorMenuLinks]);

    const items = useMemo<InternalLinkSection[]>(() => {
        const items: InternalLinkSection[] = [
            ...splitMenuCollectionsToSections(collections),
            {
                key: 'page',
                title: 'Страницы',
                icon: MenuItemLinkTypeIcon.page,
                options: pages.map(({ id, title }) => {
                    return { value: id, label: title, content: title };
                }),
            },
            {
                key: 'category',
                title: 'Категории',
                icon: MenuItemLinkTypeIcon.category,
                options: mapMenuCategoriesToTreeForSelect(categories),
            },
        ];

        if (!trimmedSearchString) {
            return items;
        }

        return items
            .map(({ options, ...section }) => {
                return {
                    ...section,
                    options: options
                        .filter((option) => trimAndLowerString(option.label).includes(trimmedSearchString))
                        .map((option) => {
                            return { ...option, content: option.label };
                        }),
                };
            })
            .filter(({ options }) => options.length > 0);
    }, [categories, collections, pages, trimmedSearchString]);

    return {
        isLoading: isFetchingCategories,
        items,
        categories,
        pages,
        collections,
    };
};
